import { useThemeMode } from '../context/ThemeContext';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  Divider,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  alpha,
  InputAdornment,
  TextField,
  Paper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Construction as MaterialsIcon,
  LocationOn as SitesIcon,
  People as UsersIcon,
  Assessment as ReportsIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  Logout as LogoutIcon,
  AccountCircle,
  KeyboardArrowDown,
  SwapHoriz,
  Email as EmailIcon,
  AssignmentInd as ChangeRequestIcon,
  HelpOutline as SupportIcon,
  Assignment as AssignmentIcon,
  ConfirmationNumber as TicketIcon,
  Compare as CompareIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import FloatingHelpButton from './FloatingHelpButton';

const drawerWidth = 240;

const DashboardLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout, isAdmin, isManager } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleTheme } = useThemeMode();
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const searchRoutes = [
    { label: 'Material Logs', path: '/materials', icon: <MaterialsIcon fontSize="small" /> },
    { label: 'Executive Portfolio', path: '/executive', icon: <ReportsIcon fontSize="small" /> },
    { label: 'Inventory', path: '/inventory', icon: <InventoryIcon fontSize="small" /> },
    { label: 'Sites', path: '/sites', icon: <SitesIcon fontSize="small" /> },
    { label: 'BOQ Management', path: '/boq', icon: <AssignmentIcon fontSize="small" /> },
    { label: 'Multi-Sheet BOQ', path: '/boq-workbook', icon: <AssignmentIcon fontSize="small" /> },
    { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon fontSize="small" /> },
    { label: 'Analytics', path: '/reports', icon: <ReportsIcon fontSize="small" /> },
    { label: 'Transfers', path: '/transfers', icon: <SwapHoriz fontSize="small" /> },
    { label: 'Team / Users', path: '/users', icon: <UsersIcon fontSize="small" /> },
    { label: 'Support', path: '/support', icon: <SupportIcon fontSize="small" /> },
  ];

  const searchResults = globalSearch.trim()
    ? searchRoutes.filter(r =>
      r.label.toLowerCase().includes(globalSearch.toLowerCase())
    )
    : [];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Sites', icon: <SitesIcon />, path: '/sites' },
  ];

  if (isAdmin || isManager) {
    menuItems.push({ text: 'Team', icon: <UsersIcon />, path: '/users' });
    menuItems.push({ text: 'BOQ Management', icon: <AssignmentIcon />, path: '/boq' });
  }

  if (isAdmin || isManager) {
    menuItems.push({ text: 'Executive Portfolio', icon: <ReportsIcon />, path: '/executive' });
  }

  if (isAdmin || isManager) {
  menuItems.push({ text: 'Vendor Comparison', icon: <CompareIcon />, path: '/vendor-comparison' });
}

if (isAdmin || isManager) {
  menuItems.push({ text: 'Vendors', icon: <BusinessIcon />, path: '/vendors' });
}

  menuItems.push({ text: 'Material Logs', icon: <MaterialsIcon />, path: '/materials' });
  menuItems.push({ text: 'Inventory', icon: <InventoryIcon />, path: '/inventory' });
  menuItems.push({ text: 'Analytics', icon: <ReportsIcon />, path: '/reports' });
  menuItems.push({ text: 'Transfers', icon: <SwapHoriz />, path: '/transfers' });
  menuItems.push({ text: 'Support', icon: <SupportIcon />, path: '/support' });
  menuItems.push({ text: 'My Profile', icon: <AccountCircleIcon />, path: '/profile' });

  if (user?.role === 'manager' || user?.role === 'supervisor') {
    menuItems.push({ text: 'My Change Requests', icon: <ChangeRequestIcon />, path: '/my-change-requests' });
  }

  if (isAdmin) {
    menuItems.push({ text: 'Change Requests', icon: <ChangeRequestIcon />, path: '/admin/change-requests' });
    menuItems.push({ text: 'Tickets (Admin)', icon: <TicketIcon />, path: '/admin/tickets' });
    menuItems.push({ text: 'Company Branding', icon: <PaletteIcon />, path: '/settings' });
    menuItems.push({ text: 'System Settings', icon: <SettingsIcon />, path: '/admin/settings' });
    menuItems.push({ text: 'Email Settings', icon: <EmailIcon />, path: '/admin/email' });
  }

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {/* Sidebar Header - BIGGER LOGO */}
      <Box sx={{
        p: 3,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: settings?.logoUrl ? 'column' : 'row' }}>
          {/* ✅ BIGGER LOGO - Now 80x80 */}
          {settings?.logoUrl && settings?.showLogoInHeader && (
            <Box
              component="img"
              src={settings.logoUrl}
              alt="Company Logo"
              sx={{
                height: 80,
                width: 80,
                objectFit: 'contain',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/dashboard')}
            />
          )}

          {/* Company Name and Tagline */}
          {settings?.showCompanyName && (
            <Box sx={{ textAlign: settings?.logoUrl ? 'center' : 'left', width: '100%' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: 'text.primary',
                  lineHeight: 1.2,
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/dashboard')}
              >
                {settings?.companyName || 'Material Tracker'}
              </Typography>
              {settings?.tagline && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                  {settings.tagline}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Menu Items - Clean Minimal Style */}
      <List sx={{ flexGrow: 1, px: 2, py: 2 }}>
        {menuItems.map((item) => {
          const isSelected = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isSelected}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 1.5,
                  px: 2,
                  py: 1.2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  '&.Mui-selected': {
                    bgcolor: alpha(settings?.primaryColor || '#64A8ED', 0.08),
                    color: settings?.primaryColor || '#64A8ED',
                    '&:hover': {
                      bgcolor: alpha(settings?.primaryColor || '#64A8ED', 0.12),
                    },
                    '& .MuiListItemIcon-root': {
                      color: settings?.primaryColor || '#64A8ED',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{
                  color: isSelected ? (settings?.primaryColor || '#64A8ED') : 'text.secondary',
                  minWidth: 40
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: '0.875rem',
                    color: isSelected ? 'inherit' : 'text.primary'
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
        <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ fontSize: '0.7rem' }}>
          © 2026 {settings?.companyName || 'HyLif Innovations'}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* App Bar - Clean White Header */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 56, sm: 64 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 160 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ display: { sm: 'none' }, color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600, fontSize: '1.1rem', display: { xs: 'none', sm: 'block' } }}>
              {menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
            </Typography>
          </Box>

          <ClickAwayListener onClickAway={() => { setSearchOpen(false); setGlobalSearch(''); }}>
            <Box sx={{ position: 'relative', flex: 1, maxWidth: 480, mx: 3 }}>
              <TextField
                size="small"
                placeholder="Search pages, materials, sites..."
                value={globalSearch}
                onChange={(e) => { setGlobalSearch(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                fullWidth
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                    '& fieldset': { border: 'none' },
                    '&:hover fieldset': { border: 'none' },
                    '&.Mui-focused fieldset': { border: '1px solid #64A8ED' }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: '#757575' }} />
                    </InputAdornment>
                  ),
                  endAdornment: globalSearch && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => { setGlobalSearch(''); setSearchOpen(false); }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              {/* Search Results Dropdown */}
              {searchOpen && searchResults.length > 0 && (
                <Paper elevation={4} sx={{
                  position: 'absolute',
                  top: '110%',
                  left: 0,
                  right: 0,
                  zIndex: 9999,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  minWidth: 220
                }}>
                  <List dense disablePadding>
                    {searchResults.map((result) => (
                      <ListItem key={result.path} disablePadding>
                        <ListItemButton
                          onClick={() => {
                            navigate(result.path);
                            setGlobalSearch('');
                            setSearchOpen(false);
                          }}
                          sx={{ px: 2, py: 1, '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          <ListItemIcon sx={{ minWidth: 32, color: '#64A8ED' }}>
                            {result.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={result.label}
                            primaryTypographyProps={{ fontSize: '0.875rem' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
              {searchOpen && globalSearch.trim() && searchResults.length === 0 && (
                <Paper elevation={4} sx={{
                  position: 'absolute',
                  top: '110%',
                  left: 0,
                  right: 0,
                  zIndex: 9999,
                  borderRadius: 2,
                  border: '1px solid #E8E8E8',
                  p: 2
                }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    No pages found
                  </Typography>
                </Paper>
              )}
            </Box>
          </ClickAwayListener>
          {/* Theme Toggle */}
          <IconButton onClick={toggleTheme} sx={{ color: 'text.primary', mr: 1 }}>
            {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>

          {/* Global Search */}

          {/* User Profile Menu - Minimal Style */}
          <Box
            onClick={handleMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                {user?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                {user?.role}
              </Typography>
            </Box>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: settings?.primaryColor || '#64A8ED',
                fontWeight: 600,
                fontSize: '0.875rem'
              }}
            >
              {user?.name?.charAt(0)}
            </Avatar>
            <KeyboardArrowDown sx={{ fontSize: 18, color: 'text.secondary' }} />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 2,
              sx: {
                mt: 1.5,
                minWidth: 180,
                borderRadius: 2,
                border: '1px solid #E8E8E8',
                '& .MuiMenuItem-root': {
                  borderRadius: 1,
                  mx: 1,
                  my: 0.5,
                  fontSize: '0.875rem'
                }
              }
            }}
          >
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2" fontWeight={600}>
                  {user?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.role}
                </Typography>
              </ListItemText>
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none'
            }
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              borderRight: '1px solid',
              borderColor: 'divider'
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, sm: 8 },
          minHeight: '100vh',
          bgcolor: 'background.default'
        }}
      >
        <Outlet />
      </Box>

      {/* Floating Help Button — always visible */}
      <FloatingHelpButton />
    </Box>
  );
};

export default DashboardLayout;